import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  IconButton,
  Box,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  ListItemText,
  Grid,
} from "@material-ui/core";
import { Visibility, VisibilityOff, DeleteOutline, Add } from "@material-ui/icons";
import { InputAdornment } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import { allowedModels, getModelLabel } from "../../constants/aiModels";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  toolsSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  toolCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  toolHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
}));

const emptyTool = () => ({
  name: "",
  description: "",
  method: "GET",
  url: "",
  headers: "",
  bodyTemplate: "",
  parametersSchema: "",
  enabled: true,
});

// Esquema de validação alinhado com o backend
const DialogflowSchema = Yup.object().shape({
  name: Yup.string()
    .min(5, "Muito curto!")
    .max(100, "Muito longo!")
    .required("Obrigatório"),
  prompt: Yup.string()
    .min(50, "Muito curto!")
    .required("Descreva o treinamento para Inteligência Artificial"),
  model: Yup.string()
    .oneOf(allowedModels, "Modelo inválido")
    .required("Informe o modelo"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(4096, "Máximo 4096 tokens")
    .required("Informe o número máximo de tokens"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(1, "Máximo 1")
    .required("Informe a temperatura"),
  apiKey: Yup.string(),
  maxMessages: Yup.number()
    .min(1, "Mínimo 1 mensagem")
    .max(50, "Máximo 50 mensagens")
    .required("Informe o número máximo de mensagens"),
  voice: Yup.string().when("model", {
    is: "gpt-3.5-turbo-1106",
    then: Yup.string().required("Informe o modo para Voz"),
    otherwise: Yup.string().notRequired(),
  }),
  voiceKey: Yup.string().notRequired(),
  voiceRegion: Yup.string().notRequired(),
  maxToolRounds: Yup.number().min(1).max(10).notRequired(),
  tools: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      description: Yup.string(),
      method: Yup.string().oneOf(["GET", "POST", "PUT", "PATCH", "DELETE"]),
      url: Yup.string(),
    })
  ),
  allowContactTagActions: Yup.boolean().notRequired(),
  allowRemoveContactTags: Yup.boolean().notRequired(),
  allowKanbanChange: Yup.boolean().notRequired(),
  allowedContactTagIds: Yup.array().notRequired(),
  allowedKanbanTagIds: Yup.array().notRequired(),
  enableRag: Yup.boolean().notRequired(),
  documentationIds: Yup.array().notRequired(),
  ragTopK: Yup.number().min(1).max(20).notRequired(),
  ragMinScore: Yup.number().min(0).max(1).notRequired(),
  clarifyBeforeAnswer: Yup.boolean().notRequired(),
  requireDocSearch: Yup.boolean().notRequired(),
  assessNeedToRespond: Yup.boolean().notRequired(),
});

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    prompt: "",
    model: "gpt-3.5-turbo-1106",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    maxTokens: 100,
    temperature: 1,
    apiKey: "",
    maxMessages: 10,
    tools: [],
    maxToolRounds: 5,
    allowContactTagActions: false,
    allowRemoveContactTags: false,
    allowKanbanChange: false,
    allowedContactTagIds: [],
    allowedContactTagNames: [],
    allowedKanbanTagIds: [],
    allowedKanbanTagNames: [],
    enableRag: false,
    documentationIds: [],
    documentationNames: [],
    ragTopK: 4,
    ragMinScore: 0.25,
    clarifyBeforeAnswer: false,
    requireDocSearch: false,
    assessNeedToRespond: false,
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [contactTagOptions, setContactTagOptions] = useState([]);
  const [kanbanOptions, setKanbanOptions] = useState([]);
  const [docOptions, setDocOptions] = useState([]);
  const [labels, setLabels] = useState({
    title: "Adicionar OpenAI/Gemini ao fluxo",
    btn: "Adicionar",
  });

  useEffect(() => {
    if (open !== "edit" && open !== "create") return;

    api
      .get(`/tags/list`, { params: { kanban: 0 } })
      .then(({ data: tagsData }) => {
        const tags = Array.isArray(tagsData) ? tagsData : [];
        setContactTagOptions(
          tags.map((tag) => ({ id: tag.id, name: tag.name }))
        );
      })
      .catch(() => setContactTagOptions([]));

    api
      .get(`/tag/kanban`)
      .then(({ data: kanbanData }) => {
        const lanes = Array.isArray(kanbanData?.lista)
          ? kanbanData.lista
          : Array.isArray(kanbanData)
            ? kanbanData
            : [];
        setKanbanOptions(lanes.map((tag) => ({ id: tag.id, name: tag.name })));
      })
      .catch(() => setKanbanOptions([]));

    api
      .get(`/internal-documents/ready`)
      .then(({ data: docsData }) => {
        const docs = Array.isArray(docsData?.records)
          ? docsData.records
          : Array.isArray(docsData)
            ? docsData
            : [];
        setDocOptions(docs.map((d) => ({ id: d.id, name: d.title })));
      })
      .catch(() => setDocOptions([]));
  }, [open]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar OpenAI/Gemini do fluxo",
        btn: "Salvar",
      });
      const typebotIntegration = data?.data?.typebotIntegration || {};
      const tools = Array.isArray(typebotIntegration.tools)
        ? typebotIntegration.tools.map((tool) => ({
            ...emptyTool(),
            ...tool,
            headers:
              typeof tool.headers === "object" && tool.headers
                ? JSON.stringify(tool.headers, null, 2)
                : tool.headers || "",
            parametersSchema:
              typeof tool.parametersSchema === "object" && tool.parametersSchema
                ? JSON.stringify(tool.parametersSchema, null, 2)
                : tool.parametersSchema || "",
          }))
        : [];
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        tools,
        maxToolRounds: typebotIntegration.maxToolRounds || 5,
        allowedContactTagIds: Array.isArray(typebotIntegration.allowedContactTagIds)
          ? typebotIntegration.allowedContactTagIds
          : [],
        allowedKanbanTagIds: Array.isArray(typebotIntegration.allowedKanbanTagIds)
          ? typebotIntegration.allowedKanbanTagIds
          : [],
        documentationIds: Array.isArray(typebotIntegration.documentationIds)
          ? typebotIntegration.documentationIds
          : [],
        ragTopK: typebotIntegration.ragTopK ?? 4,
        ragMinScore: typebotIntegration.ragMinScore ?? 0.25,
        model: allowedModels.includes(typebotIntegration.model)
          ? typebotIntegration.model
          : "gpt-3.5-turbo-1106",
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar OpenAI/Gemini ao fluxo",
        btn: "Adicionar",
      });
      setIntegration(initialState);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    close(null);
  };

  const parseOptionalJson = (value, label) => {
    if (!value || !String(value).trim()) return undefined;
    try {
      return JSON.parse(value);
    } catch (err) {
      throw new Error(`${label} inválido(a): use JSON válido`);
    }
  };

  const resolveNames = (ids, options) =>
    options
      .filter((opt) => (ids || []).includes(opt.id))
      .map((opt) => opt.name);

  const handleSavePrompt = (values, { setSubmitting }) => {
    try {
      const tools = (values.tools || [])
        .filter((tool) => tool.name || tool.url || tool.description)
        .map((tool) => {
          const name = String(tool.name || "").trim();
          const description = String(tool.description || "").trim();
          const url = String(tool.url || "").trim();
          if (!name || !description || !url) {
            throw new Error(
              "Cada tool precisa de nome, descrição e URL preenchidos"
            );
          }
          return {
            name,
            description,
            method: tool.method || "GET",
            url,
            headers: parseOptionalJson(tool.headers, "Headers"),
            bodyTemplate: tool.bodyTemplate || "",
            parametersSchema: parseOptionalJson(
              tool.parametersSchema,
              "Schema de parâmetros"
            ),
            enabled: tool.enabled !== false,
          };
        });

      const allowedContactTagIds = values.allowedContactTagIds || [];
      const allowedKanbanTagIds = values.allowedKanbanTagIds || [];
      const documentationIds = values.documentationIds || [];

      const promptData = {
        ...values,
        voice: values.model === "gpt-3.5-turbo-1106" ? values.voice : "texto",
        tools,
        maxToolRounds: Number(values.maxToolRounds) || 5,
        allowContactTagActions: !!values.allowContactTagActions,
        allowRemoveContactTags: !!values.allowRemoveContactTags,
        allowKanbanChange: !!values.allowKanbanChange,
        allowedContactTagIds,
        allowedContactTagNames: resolveNames(
          allowedContactTagIds,
          contactTagOptions
        ),
        allowedKanbanTagIds,
        allowedKanbanTagNames: resolveNames(allowedKanbanTagIds, kanbanOptions),
        enableRag: !!values.enableRag,
        documentationIds,
        documentationNames: resolveNames(documentationIds, docOptions),
        ragTopK: Number(values.ragTopK) || 4,
        ragMinScore:
          values.ragMinScore === "" || values.ragMinScore === undefined
            ? 0.25
            : Number(values.ragMinScore),
        clarifyBeforeAnswer: !!values.clarifyBeforeAnswer,
        requireDocSearch: !!values.requireDocSearch,
        assessNeedToRespond: !!values.assessNeedToRespond,
      };

      if (open === "edit") {
        onUpdate({
          ...data,
          data: { typebotIntegration: promptData },
        });
      } else if (open === "create") {
        promptData.projectName = promptData.name;
        onSave({
          typebotIntegration: promptData,
        });
      }
      handleClose();
    } catch (err) {
      alert(err.message || "Erro ao salvar tools");
    } finally {
      setSubmitting(false);
    }
  };

  const renderMultiSelect = ({
    label,
    name,
    value,
    options,
    setFieldValue,
  }) => (
    <FormControl fullWidth margin="dense" variant="outlined">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={value || []}
        onChange={(e) => setFieldValue(name, e.target.value)}
        renderValue={(selected) =>
          options
            .filter((t) => selected.includes(t.id))
            .map((t) => t.name)
            .join(", ")
        }
      >
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            <Checkbox
              color="primary"
              checked={(value || []).includes(opt.id)}
            />
            <ListItemText primary={opt.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={DialogflowSchema}
          onSubmit={handleSavePrompt}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("promptModal.form.name")}
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                />
                <FormControl fullWidth margin="dense" variant="outlined">
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.apikey")}
                    name="apiKey"
                    type={showApiKey ? "text" : "password"}
                    error={touched.apiKey && Boolean(errors.apiKey)}
                    helperText={
                      (touched.apiKey && errors.apiKey) ||
                      "Deixe em branco para usar a credencial padrão das Configurações"
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
                <Field
                  as={TextField}
                  label={i18n.t("promptModal.form.prompt")}
                  name="prompt"
                  error={touched.prompt && Boolean(errors.prompt)}
                  helperText={
                    (touched.prompt && errors.prompt) ||
                    "Use {quebra} na resposta da IA para enviar partes como mensagens separadas no WhatsApp."
                  }
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                  rows={10}
                  multiline
                />
                <div className={classes.multFieldLine}>
                  <FormControl
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    error={touched.model && Boolean(errors.model)}
                  >
                    <InputLabel>{i18n.t("promptModal.form.model")}</InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("promptModal.form.model")}
                      name="model"
                      onChange={(e) => {
                        setFieldValue("model", e.target.value);
                        if (e.target.value !== "gpt-3.5-turbo-1106") {
                          setFieldValue("voice", "texto");
                        }
                      }}
                    >
                      {allowedModels.map((model) => (
                        <MenuItem key={model} value={model}>
                          {getModelLabel(model)}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.model && errors.model && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {errors.model}
                      </div>
                    )}
                  </FormControl>
                  <FormControl
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    disabled={values.model !== "gpt-3.5-turbo-1106"}
                    error={touched.voice && Boolean(errors.voice)}
                  >
                    <InputLabel>{i18n.t("promptModal.form.voice")}</InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("promptModal.form.voice")}
                      name="voice"
                    >
                      <MenuItem value="texto">Texto</MenuItem>
                      <MenuItem value="pt-BR-FranciscaNeural">Francisca</MenuItem>
                      <MenuItem value="pt-BR-AntonioNeural">Antônio</MenuItem>
                      <MenuItem value="pt-BR-BrendaNeural">Brenda</MenuItem>
                      <MenuItem value="pt-BR-DonatoNeural">Donato</MenuItem>
                      <MenuItem value="pt-BR-ElzaNeural">Elza</MenuItem>
                      <MenuItem value="pt-BR-FabioNeural">Fábio</MenuItem>
                      <MenuItem value="pt-BR-GiovannaNeural">Giovanna</MenuItem>
                      <MenuItem value="pt-BR-HumbertoNeural">Humberto</MenuItem>
                      <MenuItem value="pt-BR-JulioNeural">Julio</MenuItem>
                      <MenuItem value="pt-BR-LeilaNeural">Leila</MenuItem>
                      <MenuItem value="pt-BR-LeticiaNeural">Letícia</MenuItem>
                      <MenuItem value="pt-BR-ManuelaNeural">Manuela</MenuItem>
                      <MenuItem value="pt-BR-NicolauNeural">Nicolau</MenuItem>
                      <MenuItem value="pt-BR-ValerioNeural">Valério</MenuItem>
                      <MenuItem value="pt-BR-YaraNeural">Yara</MenuItem>
                    </Field>
                    {touched.voice && errors.voice && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {errors.voice}
                      </div>
                    )}
                  </FormControl>
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.voiceKey")}
                    name="voiceKey"
                    error={touched.voiceKey && Boolean(errors.voiceKey)}
                    helperText={touched.voiceKey && errors.voiceKey}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled={values.model !== "gpt-3.5-turbo-1106"}
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.voiceRegion")}
                    name="voiceRegion"
                    error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                    helperText={touched.voiceRegion && errors.voiceRegion}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled={values.model !== "gpt-3.5-turbo-1106"}
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.temperature")}
                    name="temperature"
                    error={touched.temperature && Boolean(errors.temperature)}
                    helperText={touched.temperature && errors.temperature}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    type="number"
                    inputProps={{
                      step: "0.1",
                      min: "0",
                      max: "1",
                    }}
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.max_tokens")}
                    name="maxTokens"
                    error={touched.maxTokens && Boolean(errors.maxTokens)}
                    helperText={touched.maxTokens && errors.maxTokens}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    type="number"
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("promptModal.form.max_messages")}
                    name="maxMessages"
                    error={touched.maxMessages && Boolean(errors.maxMessages)}
                    helperText={touched.maxMessages && errors.maxMessages}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    type="number"
                  />
                </div>

                <Box className={classes.toolsSection}>
                  <div className={classes.toolHeader}>
                    <div>
                      <Typography variant="subtitle1">
                        Tools HTTP (function calling)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Disponível em modelos OpenAI. A IA pode chamar APIs
                        durante a conversa. Use {"{{campo}}"} ou {"{{arg.campo}}"}{" "}
                        na URL/body.
                      </Typography>
                    </div>
                  </div>

                  <Field
                    as={TextField}
                    label="Máx. rodadas de tools"
                    name="maxToolRounds"
                    type="number"
                    variant="outlined"
                    margin="dense"
                    style={{ maxWidth: 220 }}
                    inputProps={{ min: 1, max: 10 }}
                    helperText="Quantas vezes a IA pode chamar tools antes da resposta final"
                  />

                  <FieldArray name="tools">
                    {({ push, remove }) => (
                      <>
                        {(values.tools || []).map((tool, index) => (
                          <div className={classes.toolCard} key={index}>
                            <div className={classes.toolHeader}>
                              <Typography variant="subtitle2">
                                Tool #{index + 1}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => remove(index)}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </div>
                            <div className={classes.multFieldLine}>
                              <Field
                                as={TextField}
                                label="Nome (function)"
                                name={`tools[${index}].name`}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                placeholder="buscar_preco"
                              />
                              <FormControl
                                fullWidth
                                margin="dense"
                                variant="outlined"
                              >
                                <InputLabel>Método</InputLabel>
                                <Field
                                  as={Select}
                                  label="Método"
                                  name={`tools[${index}].method`}
                                >
                                  <MenuItem value="GET">GET</MenuItem>
                                  <MenuItem value="POST">POST</MenuItem>
                                  <MenuItem value="PUT">PUT</MenuItem>
                                  <MenuItem value="PATCH">PATCH</MenuItem>
                                  <MenuItem value="DELETE">DELETE</MenuItem>
                                </Field>
                              </FormControl>
                            </div>
                            <Field
                              as={TextField}
                              label="Descrição (quando a IA deve usar)"
                              name={`tools[${index}].description`}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              multiline
                              rows={2}
                            />
                            <Field
                              as={TextField}
                              label="URL"
                              name={`tools[${index}].url`}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              placeholder="https://api.exemplo.com/item/{{sku}}"
                            />
                            <Field
                              as={TextField}
                              label="Headers (JSON opcional)"
                              name={`tools[${index}].headers`}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              multiline
                              rows={2}
                              placeholder='{"Authorization":"Bearer ..."}'
                            />
                            <Field
                              as={TextField}
                              label="Body template (JSON opcional)"
                              name={`tools[${index}].bodyTemplate`}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              multiline
                              rows={2}
                              placeholder='{"sku":"{{sku}}"}'
                              helperText="Em POST/PUT/PATCH sem template, envia os args da IA como JSON"
                            />
                            <Field
                              as={TextField}
                              label="Parameters schema (JSON opcional)"
                              name={`tools[${index}].parametersSchema`}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              multiline
                              rows={3}
                              placeholder='{"type":"object","properties":{"sku":{"type":"string"}},"required":["sku"]}'
                            />
                          </div>
                        ))}
                        <Button
                          type="button"
                          color="primary"
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => push(emptyTool())}
                        >
                          Adicionar tool
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </Box>

                <Box className={classes.toolsSection}>
                  <Typography variant="subtitle1">Ações no CRM</Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Autoriza a IA a aplicar/remover tags e alterar a etapa do Kanban.
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={!!values.allowContactTagActions}
                          onChange={(e) =>
                            setFieldValue(
                              "allowContactTagActions",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Permitir aplicar tags no contato"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={!!values.allowRemoveContactTags}
                          onChange={(e) =>
                            setFieldValue(
                              "allowRemoveContactTags",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Permitir remover tags do contato"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={!!values.allowKanbanChange}
                          onChange={(e) =>
                            setFieldValue("allowKanbanChange", e.target.checked)
                          }
                        />
                      }
                      label="Permitir mudar etapa do Kanban"
                    />
                  </FormGroup>
                  {renderMultiSelect({
                    label: "Tags de contato autorizadas",
                    name: "allowedContactTagIds",
                    value: values.allowedContactTagIds,
                    options: contactTagOptions,
                    setFieldValue,
                  })}
                  {renderMultiSelect({
                    label: "Etapas Kanban autorizadas",
                    name: "allowedKanbanTagIds",
                    value: values.allowedKanbanTagIds,
                    options: kanbanOptions,
                    setFieldValue,
                  })}
                </Box>

                <Box className={classes.toolsSection}>
                  <Typography variant="subtitle1">
                    Comportamento de resposta
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Opções reutilizáveis para agentes de tira-dúvidas. Ligadas
                    apenas quando necessário.
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={!!values.clarifyBeforeAnswer}
                          onChange={(e) =>
                            setFieldValue(
                              "clarifyBeforeAnswer",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Perguntar antes de responder quando ambíguo"
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      style={{ marginLeft: 48, marginBottom: 8 }}
                    >
                      Se a dúvida estiver vaga, a IA faz uma pergunta específica
                      antes de responder.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={!!values.requireDocSearch}
                          disabled={!values.enableRag}
                          onChange={(e) =>
                            setFieldValue("requireDocSearch", e.target.checked)
                          }
                        />
                      }
                      label="Sempre consultar a documentação antes de responder"
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      style={{ marginLeft: 48, marginBottom: 8 }}
                    >
                      Força a busca na documentação interna (RAG) antes da
                      resposta. Requer RAG habilitado.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={!!values.assessNeedToRespond}
                          onChange={(e) =>
                            setFieldValue(
                              "assessNeedToRespond",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Avaliar se precisa responder antes de falar"
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      style={{ marginLeft: 48 }}
                    >
                      Se não houver necessidade (ex.: grupo já respondido por
                      outra pessoa, &quot;ok&quot;, conversa entre terceiros), o
                      agente fica em silêncio.
                    </Typography>
                  </FormGroup>
                </Box>

                <Box className={classes.toolsSection}>
                  <Typography variant="subtitle1">
                    Documentação interna (RAG)
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    A IA consulta trechos das documentações selecionadas para
                    responder com base no conhecimento interno.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={!!values.enableRag}
                        onChange={(e) => {
                          setFieldValue("enableRag", e.target.checked);
                          if (!e.target.checked) {
                            setFieldValue("requireDocSearch", false);
                          }
                        }}
                      />
                    }
                    label="Habilitar RAG"
                  />
                  {values.enableRag && (
                    <>
                      {renderMultiSelect({
                        label: "Documentações",
                        name: "documentationIds",
                        value: values.documentationIds,
                        options: docOptions,
                        setFieldValue,
                      })}
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Field
                            as={TextField}
                            label="Top K"
                            name="ragTopK"
                            type="number"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            inputProps={{ min: 1, max: 20 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            as={TextField}
                            label="Score mínimo"
                            name="ragMinScore"
                            type="number"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            inputProps={{
                              min: 0,
                              max: 1,
                              step: 0.05,
                            }}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  {i18n.t("promptModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderOpenAIModal;
